import ScheduleType from "./ScheduleType";
import type { AgendaGridSlot } from "./agendaTypes";
import {
  buildScheduleCoord,
  filterSchedulesForCell,
  resolveScheduleCell,
} from "./scheduleGridModel";
import type { SlotVisualKind } from "./scheduleGridTypes";
import type { HourlyWeather } from "./agendaWeatherTypes";
import { normalizeWeatherHourKey } from "./agendaWeatherHourKey";
import { formatAgendaLongDate, isoDateToDisplayDate, shiftIsoDate } from "./agendaDateFormat";
import {
  getFortalezaTodayIso,
  normalizeAgendaIsoDate,
} from "./agendaFiltersQuery";
export type MobileTimelineRowKind =
  | "scheduled"
  | "video"
  | "completed"
  | "no_show"
  | "post_visit_pending"
  | "free"
  | "blocked"
  | "mixed";

/** Status of one client inside a slot (a slot can hold several visits). */
export type MobileSlotClientStatus = "scheduled" | "video" | "pending" | "completed" | "no_show";

export type MobileSlotClient = {
  scheduleId: number;
  name: string;
  status: MobileSlotClientStatus;
};

export type MobileTimelineRow = {
  hour: string;
  hourLabel: string;
  coord: string;
  kind: MobileTimelineRowKind;
  primaryScheduleId: number;
  clientLabel: string;
  clientBadge: string | null;
  blockedNote: string | null;
  appointments: AgendaGridSlot[];
  /** One entry per visit, so several clients can be marked one by one. */
  clients: MobileSlotClient[];
};

function hasVisitOutcome(item: Pick<AgendaGridSlot, "outcome">): boolean {
  return item.outcome === "completed" || item.outcome === "no_show";
}

/** Per-client status: a visit that already started and has no result waits for marking. */
export function buildMobileSlotClients(
  appointments: AgendaGridSlot[],
  hasStarted: boolean
): MobileSlotClient[] {
  return appointments.map((item) => {
    let status: MobileSlotClientStatus;
    if (item.outcome === "completed" || item.outcome === "no_show") {
      status = item.outcome;
    } else if (hasStarted) {
      status = "pending";
    } else {
      status = item.outcome === "video" || item.video_call === 1 ? "video" : "scheduled";
    }

    return {
      scheduleId: item.id,
      name: item.client?.first_name?.trim() || "Cliente",
      status,
    };
  });
}

export type NextVisitCandidate = {
  schedule: AgendaGridSlot;
  coord: string;
  scheduleInstantMs: number;
};

function fortalezaDateParts(now: Date): {
  isoDate: string;
  minutesFromMidnight: number;
} {
  const formatter = new Intl.DateTimeFormat("en-GB", {
    timeZone: "America/Fortaleza",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

  const parts = formatter.formatToParts(now);
  const pick = (type: string) =>
    parts.find((part) => part.type === type)?.value ?? "00";

  const day = pick("day");
  const month = pick("month");
  const year = pick("year");
  const hour = Number(pick("hour"));
  const minute = Number(pick("minute"));

  return {
    isoDate: `${year}-${month}-${day}`,
    minutesFromMidnight: hour * 60 + minute,
  };
}

export function parseScheduleDateToInstantMs(scheduleDate: string): number | null {
  const normalized = scheduleDate.trim().replace(" ", "T");
  const parsed = new Date(normalized);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  return parsed.getTime();
}

export function minutesUntilSchedule(
  scheduleDate: string,
  now: Date = new Date()
): number | null {
  const instant = parseScheduleDateToInstantMs(scheduleDate);
  if (instant == null) {
    return null;
  }
  const diffMs = instant - now.getTime();
  if (diffMs <= 0) {
    return 0;
  }
  return Math.ceil(diffMs / 60_000);
}

export function isSelectedDateTodayInFortaleza(dateIso: string): boolean {
  const normalized = normalizeAgendaIsoDate(dateIso) ?? dateIso;
  return normalized === getFortalezaTodayIso();
}

export function formatMobileDayListHeading(
  dateIso: string,
  todayIso: string = getFortalezaTodayIso()
): string {
  const normalized = normalizeAgendaIsoDate(dateIso) ?? dateIso;
  if (normalized === todayIso) {
    return "Hoje";
  }
  if (normalized === shiftIsoDate(todayIso, -1)) {
    return "Ontem";
  }
  return formatAgendaLongDate(dateIso);
}

export function formatNextVisitEyebrow(
  selectedDateIso: string,
  scheduleDate: string,
  now: Date = new Date()
): string {
  if (!isSelectedDateTodayInFortaleza(selectedDateIso)) {
    const timeLabel = scheduleDate.split(" ")[1]?.slice(0, 5) ?? "";
    return timeLabel ? `PRÓXIMA · ${timeLabel}` : "PRÓXIMA VISITA";
  }

  const minutes = minutesUntilSchedule(scheduleDate, now);
  const countdown = formatMinutesUntilLabel(minutes);
  if (!countdown || countdown === "AGORA") {
    return "PRÓXIMA · AGORA";
  }
  return `PRÓXIMA · ${countdown}`;
}

export function formatMinutesUntilLabel(minutes: number | null): string | null {
  if (minutes == null) {
    return null;
  }
  if (minutes <= 0) {
    return "AGORA";
  }
  if (minutes < 60) {
    return `EM ${minutes} MIN`;
  }
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (rest === 0) {
    return `EM ${hours}H`;
  }
  return `EM ${hours}H ${rest}MIN`;
}

function scheduleMinutesOnSelectedDay(scheduleDate: string): number | null {
  const timePart = scheduleDate.split(" ")[1];
  if (!timePart) {
    return null;
  }
  const [hourStr, minuteStr] = timePart.split(":");
  const hour = Number(hourStr);
  const minute = Number(minuteStr ?? "0");
  if (Number.isNaN(hour) || Number.isNaN(minute)) {
    return null;
  }
  return hour * 60 + minute;
}

function mapVisualToMobileKind(
  visualKind: SlotVisualKind,
  params: {
    hasStarted: boolean;
    hasScheduledWithoutOutcome: boolean;
  }
): MobileTimelineRowKind {
  // Any visit without result keeps the slot pending, even when another client of the
  // same hour was already marked (the slot kind would otherwise show that result).
  if (params.hasStarted && params.hasScheduledWithoutOutcome) {
    return "post_visit_pending";
  }

  switch (visualKind) {
    case "video":
      return "video";
    case "completed":
      return "completed";
    case "no_show":
      return "no_show";
    case "mixed":
      return "mixed";
    case "scheduled":
      return "scheduled";
    case "blocked":
      return "blocked";
    case "free":
      return "free";
    default:
      return "free";
  }
}

export function buildConsultantMobileTimeline(params: {
  dateIso: string;
  consultantId: number;
  businessHours: string[];
  schedules: AgendaGridSlot[];
  now?: Date;
}): MobileTimelineRow[] {
  const { dateIso, consultantId, businessHours, schedules } = params;
  const now = params.now ?? new Date();
  const displayDate = isoDateToDisplayDate(dateIso);
  const { isoDate: todayIso, minutesFromMidnight: nowMinutes } =
    fortalezaDateParts(now);
  const isSelectedToday = dateIso === todayIso || dateIso === getFortalezaTodayIso();
  // Past days (yesterday) are fully over: visits without outcome still need marking.
  const isPastDay = !isSelectedToday && dateIso < todayIso;

  return businessHours.map((hour) => {
    const cell = resolveScheduleCell({
      dateDdMmYyyy: displayDate,
      hour,
      consultantId,
      schedules,
    });

    const hourKey = hour.split(":")[0];
    const hourMinutes = Number(hourKey) * 60;
    const hasStarted = isPastDay || (isSelectedToday && hourMinutes <= nowMinutes);

    const kind = mapVisualToMobileKind(cell.visualKind, {
      hasStarted,
      hasScheduledWithoutOutcome:
        hasStarted &&
        cell.appointments.length > 0 &&
        cell.appointments.some((item) => !hasVisitOutcome(item)),
    });

    return {
      hour,
      hourLabel: hour.slice(0, 5),
      coord: cell.coord,
      kind,
      primaryScheduleId: cell.primarySlotId,
      clientLabel: cell.clientSummary.label,
      clientBadge: cell.clientSummary.badge,
      blockedNote: cell.blockedNote,
      appointments: cell.appointments,
      clients: buildMobileSlotClients(cell.appointments, hasStarted),
    };
  });
}

export function findNextConsultantVisit(params: {
  dateIso: string;
  consultantId: number;
  businessHours: string[];
  schedules: AgendaGridSlot[];
  now?: Date;
}): NextVisitCandidate | null {
  const now = params.now ?? new Date();
  const { isoDate: todayIso, minutesFromMidnight: nowMinutes } =
    fortalezaDateParts(now);
  const displayDate = isoDateToDisplayDate(params.dateIso);
  const isFutureDay = params.dateIso > todayIso;
  const isPastDay = params.dateIso < todayIso;

  if (isPastDay) {
    return null;
  }

  let best: NextVisitCandidate | null = null;

  for (const hour of params.businessHours) {
    const cellSchedules = filterSchedulesForCell(
      params.schedules,
      params.dateIso,
      hour,
      params.consultantId
    );
    const appointments = cellSchedules.filter(
      (item) =>
        item.status === ScheduleType.SCHEDULED &&
        item.outcome !== "completed" &&
        item.outcome !== "no_show"
    );

    if (appointments.length === 0) {
      continue;
    }

    if (!isFutureDay) {
      const slotMinutes = scheduleMinutesOnSelectedDay(appointments[0].date);
      if (slotMinutes != null && slotMinutes < nowMinutes) {
        continue;
      }
    }

    const primary = appointments[0];
    const instant = parseScheduleDateToInstantMs(primary.date);
    if (instant == null) {
      continue;
    }

    const coord = buildScheduleCoord(displayDate, hour, params.consultantId);
    const candidate: NextVisitCandidate = {
      schedule: primary,
      coord,
      scheduleInstantMs: instant,
    };

    if (!best || candidate.scheduleInstantMs < best.scheduleInstantMs) {
      best = candidate;
    }
  }

  return best;
}

export function formatHourlyWeatherLine(
  hourly: HourlyWeather | null,
  hourLabel: string
): string | null {
  if (!hourly) {
    return null;
  }
  const [hour, minute = "00"] = normalizeWeatherHourKey(hourLabel).split(":");
  const displayHour = minute === "00" ? `${hour}h` : `${hour}h${minute}`;

  return `${hourly.conditionLabel} · ${Math.round(
    hourly.temperatureC
  )}°C · ${hourly.rainChance}% de chuva às ${displayHour}`;
}

export function findHourlyWeatherForSchedule(params: {
  cityName: string;
  scheduleDate: string;
  hours: HourlyWeather[];
}): HourlyWeather | null {
  const timePart = params.scheduleDate.replace("T", " ").split(" ")[1] ?? "";
  const hourKey = normalizeWeatherHourKey(timePart);
  if (!hourKey) {
    return null;
  }

  const direct = params.hours.find(
    (entry) => normalizeWeatherHourKey(entry.time) === hourKey
  );
  if (direct) {
    return direct;
  }

  return (
    params.hours.find(
      (entry) =>
        normalizeWeatherHourKey(entry.time).split(":")[0] ===
        hourKey.split(":")[0]
    ) ??
    null
  );
}

const CLIENT_STATUS_KIND: Record<MobileSlotClientStatus, MobileTimelineRowKind> = {
  scheduled: "scheduled",
  video: "video",
  pending: "post_visit_pending",
  completed: "completed",
  no_show: "no_show",
};

/**
 * One page of the multi-visit carousel: the slot seen as if it had only this client, so
 * it renders exactly like a single-visit card (title, status, outcome buttons).
 */
export function buildClientSlideRow(row: MobileTimelineRow, client: MobileSlotClient): MobileTimelineRow {
  return {
    ...row,
    kind: CLIENT_STATUS_KIND[client.status],
    primaryScheduleId: client.scheduleId,
    clientLabel: client.name,
    clientBadge: null,
    appointments: row.appointments.filter((item) => item.id === client.scheduleId),
    clients: [client],
  };
}

/**
 * The carousel opens on the first client still waiting for a result; when everyone is
 * marked, on a client who came (the hour counts as completed if anyone came).
 */
export function initialClientSlideIndex(clients: MobileSlotClient[]): number {
  const pending = clients.findIndex((client) => client.status === "pending");
  if (pending >= 0) return pending;
  const completed = clients.findIndex((client) => client.status === "completed");
  return completed >= 0 ? completed : 0;
}
